import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FileText, Home, Menu, MessageSquare, Package, ReceiptText, Tag, X } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { isAdminUser } from "../services/admin";
import PageHero from "../components/PageHero";
import heroImage from "../assets/zeamays.png";
import {
  createBlogPost,
  deleteBlogPost,
  getBlogPosts,
  updateBlogPost,
} from "../services/blog";
import { uploadBlogImage } from "../services/storage";

const emptyForm = {
  title: "",
  slug: "",
  featured_image: "",
  excerpt: "",
  content: "",
  published: true,
};

const navItems = [
  { label: "Dashboard", href: "/admin", icon: Home },
  { label: "Orders", href: "/admin/orders", icon: ReceiptText },
  { label: "Blog", href: "/admin/blog", icon: FileText },
  { label: "Products", href: "/admin/products", icon: Package },
  { label: "Reviews", href: "/admin/reviews", icon: MessageSquare },
  { label: "Promotions", href: "/admin/promotions", icon: Tag },
];

const formatDate = (dateValue) => {
  if (!dateValue) return "";

  return new Intl.DateTimeFormat("en-NG", {
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(new Date(dateValue));
};

const AdminBlog = () => {
  const { user, loading } = useAuth();
  const [authorized, setAuthorized] = useState(false);
  const [checkingAccess, setCheckingAccess] = useState(true);
  const [posts, setPosts] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingPostId, setEditingPostId] = useState("");
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState("");

  useEffect(() => {
    let cancelled = false;

    const loadPage = async () => {
      if (!user) {
        if (!cancelled) {
          setAuthorized(false);
          setCheckingAccess(false);
        }
        return;
      }

      try {
        const adminAccess = await isAdminUser(user);
        if (!cancelled) {
          setAuthorized(adminAccess);
        }

        if (adminAccess) {
          const blogPosts = await getBlogPosts({ includeUnpublished: true });
          if (!cancelled) {
            setPosts(blogPosts);
          }
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || "Unable to load blog posts.");
        }
      } finally {
        if (!cancelled) {
          setCheckingAccess(false);
        }
      }
    };

    loadPage();

    return () => {
      cancelled = true;
    };
  }, [user]);

  const editingPost = useMemo(
    () => posts.find((post) => post.id === editingPostId) || null,
    [posts, editingPostId]
  );

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setForm((current) => ({
      ...current,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const resetForm = () => {
    if (previewUrl && previewUrl.startsWith("blob:")) {
      URL.revokeObjectURL(previewUrl);
    }

    setForm(emptyForm);
    setEditingPostId("");
    setSelectedFile(null);
    setPreviewUrl("");
    setUploadError("");
    setUploadingImage(false);
    setUploadProgress(0);
    setError("");
  };

  const handleEdit = (post) => {
    setEditingPostId(post.id);
    setForm({
      title: post.title || "",
      slug: post.slug || "",
      featured_image: post.featured_image || "",
      excerpt: post.excerpt || "",
      content: post.content || "",
      published: Boolean(post.published),
    });
    setSelectedFile(null);
    setUploadError("");
    setUploadProgress(0);
    setPreviewUrl(post.featured_image || "");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (previewUrl && previewUrl.startsWith("blob:")) {
      URL.revokeObjectURL(previewUrl);
    }

    setSelectedFile(file);
    setUploadError("");
    setPreviewUrl(URL.createObjectURL(file));
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setUploadError("Choose an image before uploading.");
      return;
    }

    setUploadingImage(true);
    setUploadError("");
    setUploadProgress(0);

    try {
      const publicUrl = await uploadBlogImage(selectedFile, (progress) => setUploadProgress(progress));

      if (previewUrl && previewUrl.startsWith("blob:")) {
        URL.revokeObjectURL(previewUrl);
      }

      setForm((current) => ({ ...current, featured_image: publicUrl }));
      setPreviewUrl(publicUrl);
      setSelectedFile(null);
    } catch (err) {
      setUploadError(err.message || "Unable to upload image.");
    } finally {
      setUploadingImage(false);
      setUploadProgress(100);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");

    try {
      let featuredImage = String(form.featured_image || "").trim();

      if (selectedFile) {
        const publicUrl = await uploadBlogImage(selectedFile, (progress) => setUploadProgress(progress));
        featuredImage = publicUrl;
        setForm((current) => ({ ...current, featured_image: publicUrl }));
        setPreviewUrl(publicUrl);
        setSelectedFile(null);
      }

      const payload = {
        ...form,
        featured_image: featuredImage,
        slug: String(form.slug || "").trim(),
      };

      const savedPost = editingPostId
        ? await updateBlogPost(editingPostId, payload)
        : await createBlogPost(payload);

      setPosts((currentPosts) => {
        if (editingPostId) {
          return currentPosts.map((post) => (post.id === savedPost.id ? savedPost : post));
        }

        return [savedPost, ...currentPosts];
      });
      resetForm();
    } catch (err) {
      setError(err.message || "Unable to save blog post.");
    } finally {
      setSaving(false);
    }
  };

  const handleTogglePublished = async (post) => {
    setSaving(true);
    setError("");

    try {
      const updatedPost = await updateBlogPost(post.id, {
        ...post,
        published: !post.published,
      });
      setPosts((currentPosts) =>
        currentPosts.map((currentPost) => (currentPost.id === updatedPost.id ? updatedPost : currentPost))
      );
    } catch (err) {
      setError(err.message || "Unable to update publish status.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (postId) => {
    setSaving(true);
    setError("");

    try {
      await deleteBlogPost(postId);
      setPosts((currentPosts) => currentPosts.filter((post) => post.id !== postId));
      if (editingPostId === postId) {
        resetForm();
      }
    } catch (err) {
      setError(err.message || "Unable to delete blog post.");
    } finally {
      setSaving(false);
    }
  };

  if (loading || checkingAccess) {
    return (
      <div className="min-h-screen bg-lime-50 px-6 py-28 text-center text-slate-700">
        <p className="text-lg font-semibold">Checking admin access...</p>
      </div>
    );
  }

  if (!authorized) {
    return (
      <div className="min-h-screen bg-lime-50 px-6 py-28 text-center text-slate-700">
        <div className="mx-auto max-w-xl rounded-lg border border-red-200 bg-white p-8 shadow-sm">
          <h1 className="text-3xl font-semibold text-red-700">Access denied</h1>
          <p className="mt-4 text-lg">This page is only available to Zea Mays admin accounts.</p>
          <Link to="/" className="mt-6 inline-block rounded-md bg-red-700 px-5 py-3 text-white">
            Back to home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f7fbef] pt-16 text-slate-900">
      <div className="lg:flex">
        <aside className="fixed bottom-0 left-0 top-16 hidden w-64 border-r border-lime-200 bg-white lg:block">
          <AdminSidebar />
        </aside>

        <header className="sticky top-16 z-30 border-b border-lime-200 bg-white/95 px-4 py-3 backdrop-blur lg:hidden">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.22em] text-red-700">Zea Mays</p>
              <h1 className="text-lg font-bold text-slate-950">Blog Management</h1>
            </div>
            <button
              type="button"
              onClick={() => setMobileNavOpen((current) => !current)}
              className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-lime-200 text-red-700"
              aria-label="Toggle admin navigation"
            >
              {mobileNavOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
          {mobileNavOpen ? (
            <nav className="mt-3 grid gap-2">
              {navItems.map((item) => (
                <AdminNavLink key={item.label} item={item} onClick={() => setMobileNavOpen(false)} />
              ))}
            </nav>
          ) : null}
        </header>

        <main className="w-full px-4 py-6 sm:px-6 lg:ml-64 lg:px-8 lg:py-8">
          <div className="mx-auto max-w-7xl">
            <div className="mb-8">
              <PageHero
                eyebrow="Content studio"
                title="Write and publish stories from a polished editorial workspace."
                description="Draft blog posts, upload images, and manage publishing without leaving the admin area."
                image={heroImage}
                imageAlt="Zea Mays blog management"
                primaryAction={{ to: "/admin", label: "Back to Dashboard" }}
                secondaryAction={{ to: "/blog", label: "View Blog" }}
                stats={[
                  { label: "Posts", value: `${posts.length}` },
                  { label: "Published", value: `${posts.filter((post) => post.published).length}` },
                  { label: "Drafts", value: `${posts.filter((post) => !post.published).length}` },
                  { label: "Mode", value: "Editorial" },
                ]}
                compact
              />
            </div>

            <div className="mb-6 border-b border-lime-200 pb-6">
              <p className="text-sm font-bold uppercase tracking-[0.28em] text-red-700">Admin blog</p>
              <h2 className="mt-2 text-3xl font-bold text-slate-950 sm:text-4xl">
                Blog Management
              </h2>
              <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600">
                Write, publish, edit, and remove Zea Mays articles without touching code.
              </p>
            </div>

            {error ? (
              <div className="mb-6 rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                {error}
              </div>
            ) : null}

            <div className="grid gap-6 xl:grid-cols-[0.9fr_1.1fr]">
              <section className="rounded-lg border border-lime-200 bg-white p-5 shadow-sm">
                <h3 className="text-xl font-bold text-slate-950">
                  {editingPost ? "Edit blog post" : "Create blog post"}
                </h3>
                <form className="mt-5 space-y-4" onSubmit={handleSubmit}>
                  <Field label="Title">
                    <input
                      name="title"
                      value={form.title}
                      onChange={handleChange}
                      className="h-11 w-full rounded-md border border-lime-200 px-3 text-sm outline-none focus:border-red-600 focus:ring-2 focus:ring-red-100"
                      required
                    />
                  </Field>
                  <Field label="Slug">
                    <input
                      name="slug"
                      value={form.slug}
                      onChange={handleChange}
                      placeholder="healthy-living-tips"
                      className="h-11 w-full rounded-md border border-lime-200 px-3 text-sm outline-none focus:border-red-600 focus:ring-2 focus:ring-red-100"
                    />
                  </Field>
                  <Field label="Featured Image">
                    <div className="rounded-md border border-lime-200 bg-lime-50 p-3">
                      <div className="mb-3 overflow-hidden rounded-md border border-lime-200 bg-white">
                        {previewUrl ? (
                          <div className="aspect-[4/3] bg-white">
                            <img
                              src={previewUrl}
                              alt="Blog preview"
                              className="h-full w-full object-contain"
                            />
                          </div>
                        ) : (
                          <div className="flex aspect-[4/3] items-center justify-center bg-white text-sm text-slate-500">
                            No image selected yet.
                          </div>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <input
                          id="blog-image-file"
                          type="file"
                          accept="image/*"
                          onChange={handleFileChange}
                          className="hidden"
                        />
                        <label
                          htmlFor="blog-image-file"
                          className="cursor-pointer rounded-md border border-lime-200 bg-white px-3 py-2 text-sm font-semibold text-red-700"
                        >
                          Choose image
                        </label>
                        <button
                          type="button"
                          onClick={handleUpload}
                          disabled={uploadingImage || !selectedFile}
                          className="rounded-md bg-red-700 px-3 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:bg-slate-300"
                        >
                          {uploadingImage ? `Uploading (${uploadProgress}%)` : "Upload image"}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            if (previewUrl && previewUrl.startsWith("blob:")) {
                              URL.revokeObjectURL(previewUrl);
                            }
                            setSelectedFile(null);
                            setPreviewUrl(form.featured_image || "");
                            setUploadError("");
                          }}
                          className="rounded-md border border-lime-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700"
                        >
                          Clear
                        </button>
                      </div>
                      {uploadError ? <p className="mt-3 text-sm text-red-600">{uploadError}</p> : null}
                      <p className="mt-3 text-xs leading-5 text-slate-500">
                        Uploads are stored in the blog-images bucket and are automatically linked to the post.
                      </p>
                    </div>
                    <input
                      name="featured_image"
                      value={form.featured_image}
                      readOnly
                      placeholder="https://example.com/image.jpg"
                      className="mt-3 h-11 w-full rounded-md border border-lime-200 bg-slate-50 px-3 text-sm outline-none"
                    />
                  </Field>
                  <Field label="Excerpt">
                    <textarea
                      name="excerpt"
                      value={form.excerpt}
                      onChange={handleChange}
                      rows="3"
                      className="w-full rounded-md border border-lime-200 px-3 py-2 text-sm outline-none focus:border-red-600 focus:ring-2 focus:ring-red-100"
                    />
                  </Field>
                  <Field label="Content">
                    <textarea
                      name="content"
                      value={form.content}
                      onChange={handleChange}
                      rows="12"
                      className="w-full rounded-md border border-lime-200 px-3 py-2 text-sm leading-6 outline-none focus:border-red-600 focus:ring-2 focus:ring-red-100"
                      required
                    />
                  </Field>
                  <label className="flex items-center gap-3 rounded-md border border-lime-200 bg-lime-50 px-3 py-3 text-sm font-semibold text-slate-700">
                    <input
                      type="checkbox"
                      name="published"
                      checked={form.published}
                      onChange={handleChange}
                      className="h-4 w-4 accent-red-700"
                    />
                    Published
                  </label>
                  <div className="flex flex-col gap-2 sm:flex-row">
                    <button
                      type="submit"
                      disabled={saving}
                      className="rounded-md bg-red-700 px-5 py-3 text-sm font-semibold text-white transition hover:bg-green-600 disabled:cursor-not-allowed disabled:bg-slate-300"
                    >
                      {saving ? "Saving..." : "Save"}
                    </button>
                    {editingPost ? (
                      <button
                        type="button"
                        onClick={resetForm}
                        className="rounded-md border border-lime-200 px-5 py-3 text-sm font-semibold text-red-700 transition hover:bg-lime-50"
                      >
                        Cancel edit
                      </button>
                    ) : null}
                  </div>
                </form>
              </section>

              <section className="rounded-lg border border-lime-200 bg-white shadow-sm">
                <div className="border-b border-lime-200 px-5 py-4">
                  <h3 className="text-xl font-bold text-slate-950">All blog posts</h3>
                  <p className="text-sm text-slate-500">{posts.length} posts</p>
                </div>
                <div className="divide-y divide-lime-100">
                  {posts.length ? (
                    posts.map((post) => (
                      <article key={post.id} className="p-5">
                        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                          <div>
                            <div className="flex flex-wrap items-center gap-2">
                              <span
                                className={`rounded-full px-3 py-1 text-xs font-bold ${
                                  post.published
                                    ? "bg-green-50 text-green-700"
                                    : "bg-amber-50 text-amber-700"
                                }`}
                              >
                                {post.published ? "Published" : "Draft"}
                              </span>
                              <span className="text-sm text-slate-500">{formatDate(post.created_at)}</span>
                            </div>
                            <h4 className="mt-3 text-lg font-bold text-slate-950">{post.title}</h4>
                            <p className="mt-2 line-clamp-2 text-sm leading-6 text-slate-600">
                              {post.excerpt || post.content}
                            </p>
                          </div>
                          <div className="flex flex-wrap gap-2 md:justify-end">
                            <Link
                              to={`/blog/${post.id}`}
                              className="rounded-md border border-lime-200 px-3 py-2 text-sm font-semibold text-red-700 transition hover:bg-lime-50"
                            >
                              View
                            </Link>
                            <button
                              type="button"
                              onClick={() => handleEdit(post)}
                              className="rounded-md border border-lime-200 px-3 py-2 text-sm font-semibold text-red-700 transition hover:bg-lime-50"
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              onClick={() => handleTogglePublished(post)}
                              disabled={saving}
                              className="rounded-md bg-slate-900 px-3 py-2 text-sm font-semibold text-white transition hover:bg-slate-700 disabled:cursor-not-allowed disabled:bg-slate-300"
                            >
                              {post.published ? "Unpublish" : "Publish"}
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDelete(post.id)}
                              disabled={saving}
                              className="rounded-md bg-red-700 px-3 py-2 text-sm font-semibold text-white transition hover:bg-red-800 disabled:cursor-not-allowed disabled:bg-slate-300"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      </article>
                    ))
                  ) : (
                    <div className="p-8 text-center text-slate-500">No blog posts yet.</div>
                  )}
                </div>
              </section>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="mb-2 block text-sm font-semibold text-slate-700">{label}</span>
    {children}
  </label>
);

const AdminSidebar = () => (
  <div className="flex h-full flex-col px-5 py-6">
    <div className="border-b border-lime-200 pb-6">
      <p className="text-xs font-bold uppercase tracking-[0.28em] text-red-700">Zea Mays</p>
      <h2 className="mt-2 text-2xl font-bold text-slate-950">Admin</h2>
    </div>
    <nav className="mt-6 grid gap-2">
      {navItems.map((item) => (
        <AdminNavLink key={item.label} item={item} />
      ))}
    </nav>
  </div>
);

const AdminNavLink = ({ item, onClick }) => {
  const Icon = item.icon;

  return (
    <Link
      to={item.href}
      onClick={onClick}
      className="flex items-center gap-3 rounded-md px-3 py-3 text-sm font-semibold text-slate-700 transition hover:bg-lime-100 hover:text-red-700"
    >
      <Icon size={18} />
      {item.label}
    </Link>
  );
};

export default AdminBlog;
